import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';

interface StrengthLabels {
  weak: string;
  medium: string;
  strong: string;
  veryStrong: string;
}

@Component({
  selector: 'vrm-password-strength',
  templateUrl: './vrm-password-strength.component.html',
  styleUrls: ['./vrm-password-strength.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VrmPasswordStrengthComponent implements OnChanges {
  @Input() public label: string | null;
  @Input() public password: string | null;
  @Input() public strengthLabels: StrengthLabels;

  private thresholds = [90, 70, 40, 20];
  private colors = ['#F00', '#F90', '#FF0', '#9F0', '#0F0'];
  public strengthLabel: string | null;
  public bar0Color: string;
  public bar1Color: string;
  public bar2Color: string;
  public bar3Color: string;
  public bar4Color: string;

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.setBarColors(5, null);
    this.setStrengthLabel(null);

    if (!changes.password?.currentValue) {
      return;
    }

    const password = changes.password.currentValue;

    if (password) {
      const result = this.getStrengthIndexAndColor(password);
      this.setStrengthLabel(result.strengthLabel);
      this.setBarColors(result.idx, result.col);
    }
  }

  private getStrengthIndexAndColor(password: string) {
    return this.getResult(this.measureStrength(password));
  }

  private getResult(score: number) {
    let idx = 0;
    let strengthLabel: string;

    if (score > this.thresholds[0]) {
      idx = 4;
      strengthLabel = 'veryStrong';
    } else if (score > this.thresholds[1]) {
      idx = 3;
      strengthLabel = 'strong';
    } else if (score >= this.thresholds[2]) {
      idx = 2;
      strengthLabel = 'medium';
    } else if (score >= this.thresholds[3]) {
      idx = 1;
      strengthLabel = 'weak';
    }

    return {
      idx: idx + 1,
      strengthLabel,
      col: this.colors[idx],
    };
  }

  private measureStrength(pass: string) {
    let score = 0;

    const letters = {};
    for (let i = 0; i < pass.length; i++) {
      letters[pass[i]] = (letters[pass[i]] || 0) + 1;
      score += 5.0 / letters[pass[i]];
    }

    const variations = {
      digits: /\d/.test(pass),
      lower: /[a-z]/.test(pass),
      upper: /[A-Z]/.test(pass),
      nonWords: /\W/.test(pass),
    };

    let variationCount = 0;
    for (let check in variations) {
      variationCount += variations[check] ? 1 : 0;
    }

    score += (variationCount - 1) * 10;

    return Math.trunc(score);
  }

  private setBarColors(count: number, col: string) {
    for (let _n = 0; _n < count; _n++) {
      this[`bar${_n}Color`] = col;
    }
  }

  private setStrengthLabel(key: string) {
    if (this.strengthLabels) {
      this.strengthLabel = this.strengthLabels[key];
    } else {
      this.strengthLabel = null;
    }
  }
}
